import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { EnumHintKey } from '@data-layer/enums/common';
import { enumShippingRole } from '@data-layer/enums/user';
import { enumLclCardStyle } from '@shared/lcl-mutual/enums';
import { Select } from '@ngxs/store';
import { SharedSelectors } from '@app/shared/shared-state';
import { Observable, ReplaySubject } from 'rxjs';
import { HintModel } from '@data-layer/models/common';
import { takeUntil } from 'rxjs/operators';
import { GenericService } from '@services/generic.service';

@Component({
  selector: 'lcl-incoterms',
  templateUrl: './lcl-incoterms.component.html',
  styleUrls: ['./lcl-incoterms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclIncotermsComponent implements OnInit, OnDestroy {
  @Input() public incoterms: typeof enumIncotermsType;
  @Input() public companyShippingRole: enumShippingRole = enumShippingRole.OTHER;

  @Output() public changeIncotermsSource: EventEmitter<enumIncotermsType> = new EventEmitter<enumIncotermsType>();
  @Output() public changeShippingRole: EventEmitter<enumShippingRole> = new EventEmitter<enumShippingRole>();

  @Select(SharedSelectors.getAllHints) public hints$: Observable<HintModel[]>;

  public formGroup: UntypedFormGroup;
  public incotermsControl: UntypedFormControl;
  public shippingRoleControl: UntypedFormControl;
  public currentShippingRole: enumShippingRole;
  public enumIncotermsType: typeof enumIncotermsType = enumIncotermsType;
  public enumHintKey: typeof EnumHintKey = EnumHintKey;
  public enumLclCardStyle: typeof enumLclCardStyle = enumLclCardStyle;

  private messages: HintModel[] = [];
  private otherShippingRoles: enumShippingRole[] = [enumShippingRole.OTHER, enumShippingRole.AGENT];
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private formBuilder: UntypedFormBuilder, private genericService: GenericService) {}

  public ngOnInit(): void {
    this.currentShippingRole = this.otherShippingRoles.includes(this.companyShippingRole)
      ? enumShippingRole.IMPORTER
      : this.companyShippingRole;

    this.createForm();
    this.incotermsControl = this.formGroup.get('incoterms') as UntypedFormControl;
    this.shippingRoleControl = new UntypedFormControl(!(this.companyShippingRole !== enumShippingRole.SHIPPER));

    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: { incoterms: enumIncotermsType }) => {
      this.changeIncotermsSource.emit(value.incoterms);

      setTimeout(() => this.genericService.recalcHints$.next());
    });
    this.shippingRoleControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateShippingRole();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public updateShippingRole(): void {
    const newRole: enumShippingRole = this.isImporter ? enumShippingRole.SHIPPER : enumShippingRole.IMPORTER;

    this.changeShippingRole.emit(newRole);
    this.currentShippingRole = newRole;
    this.incotermsControl.setValue(newRole === enumShippingRole.IMPORTER ? this.enumIncotermsType.FOB : this.enumIncotermsType.DAT);
  }

  public get isImporter(): boolean {
    return this.currentShippingRole === enumShippingRole.IMPORTER;
  }

  public get isLeftSwitchActive(): boolean {
    return [enumShippingRole.IMPORTER, true].includes(this.currentShippingRole);
  }

  public get isRightSwitchActive(): boolean {
    return [enumShippingRole.SHIPPER, false].includes(this.currentShippingRole);
  }

  public get incotermsHeaderLabel(): string {
    switch (this.formGroup?.value?.incoterms) {
      case enumIncotermsType.FCA:
        return 'LABELS.FCA_LABEL';
      case enumIncotermsType.CPT_CFR:
        return 'LABELS.CPT_CFR_LABEL';
      case enumIncotermsType.DAT:
        return 'LABELS.DAT_LABEL';
      case enumIncotermsType.FOB:
        return 'LABELS.FOB_LABEL';
      default:
        return '';
    }
  }

  public get incotermsHeaderHint(): EnumHintKey {
    switch (this.formGroup?.value?.incoterms) {
      case enumIncotermsType.FCA:
        return EnumHintKey.fcaHint;
      case enumIncotermsType.CPT_CFR:
        return EnumHintKey.cptCfrHint;
      case enumIncotermsType.DAT:
        return EnumHintKey.datHint;
      case enumIncotermsType.FOB:
        return EnumHintKey.fobHint;
      default:
        return;
    }
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      incoterms: [
        !!this.incoterms
          ? this.incoterms
          : this.currentShippingRole === enumShippingRole.SHIPPER
          ? this.enumIncotermsType.DAT
          : this.enumIncotermsType.FOB,
      ],
    });
    if (!this.incoterms) {
      this.changeIncotermsSource.emit(this.formGroup.value.incoterms);
    }
  }
}
